import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  FormControl,
  Grid,
  TextField,
  Autocomplete,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import { addContractLegal, getContractLegal, updateContractLegal } from "../../../services/backend/ContractLegalService";
import { annexesGroup } from "../../../services/backend/CorrespondenceService";
import { fetchEntityOptions } from "../../../services/backend/EntityService";
import {
  extractContractData,
  extractStartupData,
  parseAiDate,
  parseAiNumber,
} from "../../../services/backend/ContractExtractionService";
import { normalizeNit } from "../../entities/entityLogic";
import { CONTRACT_CODE_REGEX, CONTRACT_STATUS, PERMISSIONS, validateContractCodeForUser } from "../../../constants";
import {
  getContractConfig,
  DEFAULT_CONTRACT_CONFIG,
  dependencyOptions,
  natureOptionsForCode,
  modalityOptions,
  typesForModality,
  allDependencyEntries,
  allNatureEntriesForCode,
  allModalityEntries,
  allTypeEntriesForModality,
  isInactiveValue,
} from "../../../services/backend/ContractConfigService";
import { getContractField } from "../../../functions";
import CircularProgress from "../../generic/CircularProgress";
import MyBackdrop from "../../generic/MyBackdrop";
import MySnackbar from "../../generic/MySnackbar";
import MoneyInput from "../../generic/MoneyInput";
import SelectUser from "../../generic/SelectUser";
import AnnexesUploader from "../../generic/AnnexesUploader";
import DialogConfirmation from "../../generic/DialogConfirmation";
import ContractFormPanel from "../ContractFormPanel";
import ContractPersonTypeToggle from "../ContractPersonTypeToggle";
import ContractNews from "../ContractNews";
import TasksModal from "../TasksModal";
import LegalPaymentPlanModal from "./LegalPaymentPlanModal";

// Formulario de contratos de PERSONAS JURÍDICAS (colección contractsLegal).
// Estructura espejo de ContractForm.js (personas naturales) SIN refactorizarlo:
// compone los mismos componentes hijos (ContractFormPanel, TasksModal,
// AnnexesUploader, SelectUser, MoneyInput) con las diferencias del flujo
// jurídico — sin honorarios mensuales ni ARL, contratista = entidad (NIT),
// plan de pagos manual y OPCIONAL para aprobar (R6), supervisores con rol
// SUPERVISOR_LEGAL_CONTRACT. Mantener el contrato de esquema compartido
// (ver openspec/changes/legal-contracts-core/design.md).

const ContractLegalForm = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.value);
  const users = useSelector((state) => state.user.users);
  const { contractId } = useParams();

  const [approvedAt, setApprovedAt] = useState(null);
  const [contractCode, setContractCode] = useState("");
  const [contractSubject, setContractSubject] = useState("");
  const [contractBudget, setContractBudget] = useState("");
  const [contractPeriod, setContractPeriod] = useState("");
  const [dependency, setDependency] = useState(null);
  const [nature, setNature] = useState(null);
  const [modality, setModality] = useState(null);
  const [modalityContractType, setModalityContractType] = useState(null);
  const [entityOption, setEntityOption] = useState(null);
  const [entitySnapshot, setEntitySnapshot] = useState(null);
  const [entityOptions, setEntityOptions] = useState([]);
  const [documentText, setDocumentText] = useState();
  // Datos crudos devueltos por la IA a partir del OCR del PDF del contrato.
  const [extractedData, setExtractedData] = useState();
  const [fileUrl, setFileUrl] = useState("");
  const [fileHashes, setFileHashes] = useState({});
  const [startupUrl, setStartupUrl] = useState(null);
  const [designationUrl, setDesignationUrl] = useState(null);
  const [liquidationUrl, setLiquidationUrl] = useState(null);
  const [startedAt, setStartedAt] = useState(null);
  const [finishedAt, setFinishedAt] = useState(null);
  const [secopUrl, setSecopUrl] = useState("");
  const [status, setStatus] = useState(CONTRACT_STATUS.DRAFT);
  const [supervisors, setSupervisors] = useState([]);
  const [supervisorIDs, setSupervisorIDs] = useState([]);
  const [news, setNews] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [annexes, setAnnexes] = useState([]);
  const [paymentPlan, setPaymentPlan] = useState([]);
  const [paymentPlanLabels, setPaymentPlanLabels] = useState([]);
  const [creatorId, setCreatorId] = useState("");

  const [catalogCfg, setCatalogCfg] = useState(DEFAULT_CONTRACT_CONFIG);
  const [showPaymentPlanModal, setShowPaymentPlanModal] = useState(false);
  const [showTasksModal, setShowTasksModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openBackdrop, setOpenBackdrop] = useState();
  const [infoSnack, setInfoSnack] = useState({ open: false });
  const [sendSupervisorInfo, setSendSupervisorInfo] = useState({ open: false });
  const [returnInfo, setReturnInfo] = useState({ open: false });
  const [returnMessage, setReturnMessage] = useState("");

  const isSupervisor = supervisorIDs.includes(user.userId);
  const isAdmin = user.roles.includes("ADMIN");
  const isManager = user.permissions.includes(PERMISSIONS.CONTRACT_LEGAL.UPDATE);

  // Claves que el resolvedor compartido deriva de buildPaymentCalendar
  // (src/functions.js): ese calendario proyecta el ÍNDICE del paymentPlan sobre
  // los meses del contrato desde startedAt. En el plan jurídico el índice
  // identifica un HITO, no un mes (Decisión 7 de legal-contracts-core), así que
  // calcularlas aquí no daría error: daría un número plausible y equivocado.
  // Se resuelven de forma neutra y el gestor ajusta los hitos a mano.
  const MANUAL_PLAN_KEYS = { paymentPlan: () => paymentPlan, dailyRate: () => 0, balance: () => 0 };

  const getContractFieldWithNews = useCallback((key, limitDate) => {
    if (MANUAL_PLAN_KEYS[key]) return MANUAL_PLAN_KEYS[key]();

    return getContractField(news, {
      contractBudget, contractPeriod, finishedAt, news, paymentPlan,
      startedAt, status, supervisors, tasks,
    }, limitDate)(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [news, contractBudget, contractPeriod, finishedAt, paymentPlan, startedAt, status, supervisors, tasks]);

  useEffect(() => {
    (async () => {
      setCatalogCfg(await getContractConfig());
      const { items } = await fetchEntityOptions();
      setEntityOptions(items);
    })();
  }, []);

  // Cargar el contrato en edición.
  useEffect(() => {
    if (!contractId) return;
    (async () => {
      setOpenBackdrop("Cargando contrato");
      const response = await getContractLegal(contractId, user);
      if (!response) {
        setOpenBackdrop(undefined);
        setInfoSnack({ open: true, message: "No fue posible cargar el contrato", severity: "error" });
        return navigate("/contracts");
      }
      setApprovedAt(response.approvedAt);
      setContractCode(response.contractCode);
      setContractSubject(response.contractSubject || "");
      setContractBudget(response.contractBudget || "");
      setContractPeriod(response.contractPeriod || "");
      setDependency(response.dependency || null);
      setNature(response.nature || null);
      setModality(response.modality || null);
      setModalityContractType(response.modalityContractType || null);
      setEntitySnapshot(response.entity || null);
      setDocumentText(response.documentText);
      setFileUrl(response.fileUrl || "");
      setFileHashes(response.fileHashes || {});
      setStartupUrl(response.startupUrl || null);
      setDesignationUrl(response.designationUrl || null);
      setLiquidationUrl(response.liquidationUrl || null);
      setStartedAt(response.startedAt);
      setFinishedAt(response.finishedAt);
      setSecopUrl(response.secopUrl || "");
      setStatus(response.status);
      setSupervisors(response.supervisors || []);
      setSupervisorIDs(response.supervisorIDs || []);
      setNews(response.news || []);
      setTasks(response.tasks || []);
      setAnnexes(response.annexes || []);
      setPaymentPlan(response.paymentPlan || []);
      setPaymentPlanLabels(response.paymentPlanLabels || []);
      setCreatorId(response.userId || "");
      setOpenBackdrop(undefined);
    })();
    // eslint-disable-next-line
  }, [contractId]);

  // Guard de acceso: gestor (CONTRACT.UPDATE), ADMIN o supervisor asignado.
  useEffect(() => {
    if (!contractId || !supervisorIDs.length) return;
    if (!isAdmin && !isManager && !isSupervisor) navigate("/contracts");
    // eslint-disable-next-line
  }, [supervisorIDs]);

  // Mismas reglas de editabilidad de clasificación que el flujo natural (C1).
  const canEditClassification =
    [CONTRACT_STATUS.DRAFT, CONTRACT_STATUS.SUPERVISOR].includes(status) &&
    (status === CONTRACT_STATUS.SUPERVISOR || isManager);

  const canEditBase = [CONTRACT_STATUS.DRAFT, CONTRACT_STATUS.SUPERVISOR].includes(status);

  const withCurrent = (opts, current) =>
    current && !opts.includes(current) ? [...opts, current] : opts;

  const dependencyOpts = useMemo(
    () => withCurrent(dependencyOptions(catalogCfg), dependency),
    [catalogCfg, dependency]
  );
  const natureOpts = useMemo(
    () => withCurrent(natureOptionsForCode(catalogCfg, contractCode), nature),
    [catalogCfg, contractCode, nature]
  );
  const modalityOpts = useMemo(
    () => withCurrent(modalityOptions(catalogCfg), modality),
    [catalogCfg, modality]
  );
  const modalityTypeOpts = useMemo(
    () => withCurrent(typesForModality(catalogCfg, modality), modalityContractType),
    [catalogCfg, modality, modalityContractType]
  );

  const inactiveLabel = (entries) => (option) =>
    isInactiveValue(entries, option) ? `${option} (inactivo)` : option;

  // Autollenado con IA del PDF del contrato jurídico. Comparte el servicio de
  // extracción con el flujo natural (ContractExtractionService) con el prompt
  // de la variante jurídica: contratista = EMPRESA (NIT + razón social), sin
  // honorarios mensuales ni cédula de contratista.
  const handleGetData = async (text) => {
    setOpenBackdrop("Extrayendo información del contrato");
    const data = await extractContractData(text, { legal: true });
    setOpenBackdrop(undefined);

    if (!data) {
      return setInfoSnack({
        open: true,
        message: "No fue posible extraer la información del contrato: diligencia los campos manualmente.",
        severity: "warning",
        duration: 6000,
      });
    }

    setExtractedData(data);
  };

  const handleGetDataStartup = async (text) => {
    setOpenBackdrop("Extrayendo información del acta de inicio");
    const data = await extractStartupData(text);
    setOpenBackdrop(undefined);

    if (!data) {
      return setInfoSnack({
        open: true,
        message: "No fue posible extraer la información del acta de inicio: diligencia los campos manualmente.",
        severity: "warning",
        duration: 6000,
      });
    }

    const startedAtValue = parseAiDate(data.startedAt);
    const finishedAtValue = parseAiDate(data.finishedAt);
    const period = parseAiNumber(data.contractPeriod);

    if (startedAtValue) setStartedAt(startedAtValue);
    if (period) setContractPeriod(period);
    if (finishedAtValue) setFinishedAt(finishedAtValue);
  };

  // Dispara la extracción al cargar el PDF de un contrato nuevo. `documentText`
  // solo cambia por la carga (al editar se hidrata desde Firestore, y ahí
  // contractId ya existe).
  useEffect(() => {
    if (documentText && !contractId && !extractedData) handleGetData(documentText);
    // eslint-disable-next-line
  }, [documentText]);

  // Aplica lo extraído SOLO sobre los campos vacíos: lo que el gestor ya
  // diligenció manda. Cada valor se valida antes de asignarse — el modelo
  // devuelve null en lo que no identifica y moment(null) daría "hoy".
  useEffect(() => {
    if (!extractedData || contractId) return;

    const data = extractedData;
    const period = parseAiNumber(data.contractPeriod);
    const budget = parseAiNumber(data.contractBudget);
    const approvedAtValue = parseAiDate(data.approvedAt);
    const finishedAtValue = parseAiDate(data.finishedAt);

    if (!contractCode && data.contractCode) setContractCode(String(data.contractCode).trim().toUpperCase());
    if (!contractSubject && data.contractSubject) setContractSubject(data.contractSubject);
    if (!contractBudget && budget) setContractBudget(budget);
    if (!contractPeriod && period) setContractPeriod(period);
    if (!approvedAt && approvedAtValue) setApprovedAt(approvedAtValue);
    if (!finishedAt && finishedAtValue) setFinishedAt(finishedAtValue);
    // El contrato no trae fecha de inicio: se deriva del cierre menos el plazo
    // (el acta de inicio la corrige después, si se carga).
    if (!startedAt && finishedAtValue && period) {
      setStartedAt(finishedAtValue.clone().subtract(period, "days"));
    }

    // La empresa contratista NO se crea desde el PDF: solo se preselecciona si
    // el NIT extraído corresponde a una entidad del selector. Se compara con
    // normalizeNit (misma normalización con la que se persiste `entityNit`),
    // así el dígito de verificación o los puntos del OCR no impiden el match.
    // fetchEntityOptions excluye las entidades PENDING e INACTIVE, así que un
    // NIT correcto puede no tener match: el aviso no afirma que no exista.
    const extractedNit = normalizeNit(data.entityNit);
    if (!entityOption && extractedNit) {
      const match = entityOptions.find((opt) => normalizeNit(opt.nit) === extractedNit);
      if (match) setEntityOption(match);
      else {
        setInfoSnack({
          open: true,
          message: `La empresa con NIT ${extractedNit}${data.entityName ? ` (${data.entityName})` : ""} no está disponible en el selector: verifica que esté registrada y aprobada en Entidades.`,
          severity: "info",
          duration: 8000,
        });
      }
    }
    // eslint-disable-next-line
  }, [extractedData]);

  // supervisorIDs es ACUMULATIVO (historial), no la lista vigente: el servidor
  // consulta `supervisorIDs array-contains userId` y solo después post-filtra
  // por supervisor efectivo. Si el entrante de una novedad no entrara aquí, la
  // query nunca le devolvería el contrato. Mismo patrón que ContractForm.js.
  const buildSupervisorIDs = () => {
    const ids = supervisors.map((s) => s.userId);
    news
      .filter((n) => ["CHANGE_SUPERVISOR", "ASSIGN_SUPERVISOR"].includes(n.type) && n.supervisor)
      .forEach((n) => {
        if (!ids.includes(n.supervisor.userId)) ids.push(n.supervisor.userId);
      });

    return ids;
  };

  const buildData = (overrides = {}) => ({
    annexes,
    approvedAt,
    contractCode,
    contractSubject,
    contractBudget,
    contractPeriod,
    dependency,
    documentText: documentText || null,
    entityNit: entityOption?.nit || entitySnapshot?.nit,
    fileUrl,
    fileHashes,
    finishedAt,
    liquidationUrl,
    modality,
    modalityContractType,
    nature,
    news,
    paymentPlan,
    paymentPlanLabels,
    secopUrl,
    startedAt,
    startupUrl,
    designationUrl,
    status,
    supervisors,
    supervisorIDs: buildSupervisorIDs(),
    tasks,
    ...overrides,
  });

  const businessName = entitySnapshot?.legalName || entityOption?.legalName || "Sin asignar";

  const handleSubmit = async (evt) => {
    evt?.preventDefault?.();
    // Al crear, el código también debe ser de un tipo permitido al usuario
    // (contractTypes); al editar solo se revisa el formato.
    const codeError = contractId
      ? (CONTRACT_CODE_REGEX.test(contractCode) ? null : "El código de contrato no tiene un formato válido. Ejemplo: CPSDF_001_2025. Prefijos admitidos: CPSDF, RCPS, DEX")
      : validateContractCodeForUser(contractCode, user);
    let message;
    if (!contractCode) message = "Debes ingresar el código del contrato";
    else if (codeError) message = codeError;
    else if (!fileUrl) message = "Debes cargar el PDF del contrato";
    else if (!contractId && !entityOption?.nit) message = "Debes seleccionar la empresa contratista";
    if (message) return setInfoSnack({ open: true, message, severity: "warning" });

    setLoading(true);
    setOpenBackdrop("Guardando contrato");
    if (contractId) {
      const response = await updateContractLegal(contractId, buildData(), user);
      if (!response) {
        setInfoSnack({ open: true, message: "Error al actualizar el contrato", severity: "error" });
      }
    } else {
      const response = await addContractLegal({ ...buildData(), userId: user.userId }, user);
      if (response) {
        setEntitySnapshot(response.entity || null);
        if (response.fileUrl) setFileUrl(response.fileUrl);
        if (response.startupUrl) setStartupUrl(response.startupUrl);
        if (response.designationUrl) setDesignationUrl(response.designationUrl);
        setCreatorId(user.userId);
        navigate(`/contracts/legal/${contractCode}`, { replace: true });
      } else {
        setInfoSnack({
          open: true,
          message: "Error al crear el contrato (verifica que el código no exista ya en el sistema)",
          severity: "error",
        });
      }
    }
    setOpenBackdrop(undefined);
    setLoading(false);
  };

  const handleSendSupervisor = async () => {
    if (!supervisors.length) {
      return setInfoSnack({ open: true, message: "Debes seleccionar al menos un supervisor", severity: "warning" });
    }
    setOpenBackdrop("Enviando a supervisor...");
    const email = supervisors.map((s) => ({
      emailTo: s.email,
      template: "contractLegalSupervisorCheck",
      data: {
        contractCode,
        usertitle: s.usertitle,
        fullname: `${s.name} ${s.lastName}`,
        businessName,
      },
    }));
    const response = await updateContractLegal(
      contractId,
      { ...buildData({ status: CONTRACT_STATUS.SUPERVISOR }), email },
      user
    );
    if (response) setStatus(CONTRACT_STATUS.SUPERVISOR);
    else setInfoSnack({ open: true, message: "Error al enviar a supervisor", severity: "error" });
    setOpenBackdrop(undefined);
  };

  const handleApprove = async () => {
    // Espejo del natural SIN plan de pagos: no es obligatorio para aprobar (R6).
    const missingFields = [];
    if (!tasks.length) missingFields.push("Obligaciones del contratista");
    if (!dependency) missingFields.push("Dependencia");
    if (!nature) missingFields.push("Naturaleza del contrato");
    if (missingFields.length) {
      const message = (
        <div>
          <div>Debes completar los siguientes campos:</div>
          <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
            {missingFields.map((field, index) => <li key={index}>{field}</li>)}
          </ul>
        </div>
      );
      return setInfoSnack({ open: true, message, severity: "warning" });
    }

    setOpenBackdrop("Aprobando contrato...");
    const creator = users?.find((u) => u.userId === creatorId);
    const email = [];
    if (creator?.email) {
      email.push({
        emailTo: creator.email,
        template: "contractLegalApproved",
        data: { contractCode, businessName, recipientName: `${creator.name} ${creator.lastName}` },
      });
    }
    if (entitySnapshot?.email) {
      email.push({
        emailTo: entitySnapshot.email,
        template: "contractLegalApproved",
        data: { contractCode, businessName, recipientName: entitySnapshot.legalRepName || businessName },
      });
    }
    const response = await updateContractLegal(
      contractId,
      { ...buildData({ status: CONTRACT_STATUS.APPROVED }), ...(email.length ? { email } : {}) },
      user
    );
    if (response) setStatus(CONTRACT_STATUS.APPROVED);
    else setInfoSnack({ open: true, message: "Error al aprobar el contrato", severity: "error" });
    setOpenBackdrop(undefined);
  };

  const handleReturn = async ({ message }) => {
    setOpenBackdrop("Devolviendo contrato...");
    const creator = users?.find((u) => u.userId === creatorId);
    const email = creator?.email ? [{
      emailTo: creator.email,
      template: "contractLegalReturned",
      data: {
        contractCode,
        businessName,
        recipientName: `${creator.name} ${creator.lastName}`,
        message,
      },
    }] : undefined;
    const response = await updateContractLegal(
      contractId,
      { ...buildData({ status: CONTRACT_STATUS.DRAFT }), ...(email ? { email } : {}) },
      user,
      { returnMessage: message }
    );
    if (response) setStatus(CONTRACT_STATUS.DRAFT);
    else setInfoSnack({ open: true, message: "Error al devolver el contrato", severity: "error" });
    setOpenBackdrop(undefined);
  };

  return (
    <>
      <MySnackbar info={infoSnack} handleClose={() => setInfoSnack({ open: false })} />
      <MyBackdrop open={!!openBackdrop} title={openBackdrop} />

      <LegalPaymentPlanModal
        open={showPaymentPlanModal}
        onClose={() => setShowPaymentPlanModal(false)}
        canEdit={canEditBase && (isManager || isSupervisor || isAdmin)}
        // Presupuesto VIGENTE (con adiciones): tras una novedad de adición el
        // balance de hitos debe señalar la diferencia que falta por repartir.
        contractBudget={getContractFieldWithNews("contractBudget")}
        paymentPlan={paymentPlan}
        paymentPlanLabels={paymentPlanLabels}
        onSave={(values, labels) => {
          setPaymentPlan(values);
          setPaymentPlanLabels(labels);
        }}
      />

      {showTasksModal && (
        <TasksModal
          open={showTasksModal}
          contract={{ documentText, tasks, news, status }}
          noEdit={!canEditBase}
          setTasks={setTasks}
          setOpenBackdrop={setOpenBackdrop}
          getContractFieldWithNews={getContractFieldWithNews}
          onClose={() => setShowTasksModal(false)}
        />
      )}

      <DialogConfirmation
        confirmInfo={sendSupervisorInfo}
        handleCloseDialog={(result) => {
          if (result) handleSendSupervisor();
          setSendSupervisorInfo({ open: false });
        }}
      />

      <DialogConfirmation
        confirmInfo={returnInfo}
        content={(
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Motivo de la devolución"
            value={returnMessage}
            onChange={(evt) => setReturnMessage(evt.target.value)}
          />
        )}
        handleCloseDialog={(result) => {
          if (result) {
            if (!returnMessage.trim()) {
              setInfoSnack({ open: true, message: "La devolución requiere un motivo", severity: "warning" });
            } else {
              handleReturn({ message: returnMessage.trim() });
            }
          }
          setReturnInfo({ open: false });
          setReturnMessage("");
        }}
      />

      {/* El tipo de persona sigue siendo elegible mientras el contrato no
          exista: cambiarlo lleva al formulario de persona natural. */}
      {!contractId && <ContractPersonTypeToggle value="JURIDICA" />}

      <Grid container sx={{ mb: 1, height: "calc(100vh - 80px)" }}>
        <Grid item xs={6} sx={{ height: "100%", overflowY: "auto", padding: 2 }}>
          <Box component="form" onSubmit={handleSubmit} sx={{ m: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={7} sx={{ color: "gray" }}>
                Contrato — Persona Jurídica
              </Grid>
              <Grid item xs={5}>
                <Box sx={{
                  backgroundColor: "#e3eed9ff",
                  borderRadius: 5,
                  color: "gray", p: 1,
                  textAlign: "center",
                  fontSize: "0.6rem",
                }}>
                  {status}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <SelectUser
                    disabled={status !== CONTRACT_STATUS.DRAFT}
                    noedit={true}
                    userType="users"
                    filter="SUPERVISOR_LEGAL_CONTRACT"
                    placeholder="Supervisores de contrato"
                    multiple
                    value={supervisors}
                    onChange={(__, supers) => {
                      setSupervisors(supers);
                      setSupervisorIDs(supers.map((s) => s.userId));
                    }}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <Autocomplete
                    disabled={!!contractId}
                    fullWidth
                    value={contractId
                      ? (entitySnapshot ? { nit: entitySnapshot.nit, legalName: entitySnapshot.legalName } : null)
                      : entityOption}
                    options={entityOptions}
                    getOptionLabel={(option) => option?.legalName ? `${option.legalName} — NIT ${option.nit}` : ""}
                    isOptionEqualToValue={(option, value) => option?.nit === value?.nit}
                    onChange={(__, newValue) => setEntityOption(newValue)}
                    renderInput={(params) => (
                      <TextField {...params} label="Empresa contratista (Persona Jurídica)" required={!contractId} />
                    )}
                  />
                </FormControl>
                {!contractId && (
                  <Typography variant="caption" color="text.secondary">
                    ¿La empresa no está registrada? Regístrala primero en el módulo de Entidades.
                  </Typography>
                )}
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <TextField
                    required
                    label="Código de contrato"
                    value={contractCode}
                    InputProps={{ readOnly: !!contractId }}
                    onChange={(evt) => setContractCode(evt.target.value.toUpperCase())}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <DatePicker
                    label="Fecha de firma"
                    value={approvedAt}
                    readOnly={!canEditBase}
                    onChange={(val) => setApprovedAt(val)}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth>
                  <MoneyInput
                    label="Valor de contrato"
                    value={getContractFieldWithNews("contractBudget")}
                    readOnly={!canEditBase}
                    onChange={(evt) => {
                      if (canEditBase) setContractBudget(evt.target.value);
                    }}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth>
                  <DatePicker
                    label="Fecha de inicio"
                    value={startedAt}
                    readOnly={!canEditBase}
                    onChange={(val) => setStartedAt(val)}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth>
                  <DatePicker
                    label="Fecha de cierre"
                    value={getContractFieldWithNews("finishedAt")}
                    readOnly={!canEditBase}
                    onChange={(val) => setFinishedAt(val)}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth>
                  <TextField
                    type="number"
                    label="Duración (En días)"
                    value={getContractFieldWithNews("contractPeriod")}
                    InputProps={{ readOnly: !canEditBase }}
                    onChange={(evt) => setContractPeriod(evt.target.value)}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth>
                  <Autocomplete
                    readOnly={!canEditClassification}
                    fullWidth
                    value={dependency || null}
                    options={dependencyOpts}
                    getOptionLabel={inactiveLabel(allDependencyEntries(catalogCfg))}
                    onChange={(__, newValue) => setDependency(newValue)}
                    renderInput={(params) => (
                      <TextField {...params} label="Dependencia" placeholder="Dependencia" />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth>
                  <Autocomplete
                    readOnly={!canEditClassification}
                    fullWidth
                    value={nature || null}
                    options={natureOpts}
                    getOptionLabel={inactiveLabel(allNatureEntriesForCode(catalogCfg, contractCode))}
                    onChange={(__, newValue) => setNature(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={contractCode?.startsWith("DEX_") ? "Convenio (Naturaleza)" : "Naturaleza del contrato"}
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <Autocomplete
                    readOnly={!canEditClassification}
                    fullWidth
                    value={modality || null}
                    options={modalityOpts}
                    getOptionLabel={inactiveLabel(allModalityEntries(catalogCfg))}
                    onChange={(__, newValue) => {
                      setModality(newValue);
                      if (!newValue || !typesForModality(catalogCfg, newValue).includes(modalityContractType)) {
                        setModalityContractType(null);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Modalidad de contratación" />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <Autocomplete
                    readOnly={!canEditClassification}
                    disabled={!modality}
                    fullWidth
                    value={modalityContractType || null}
                    options={modalityTypeOpts}
                    getOptionLabel={inactiveLabel(allTypeEntriesForModality(catalogCfg, modality))}
                    onChange={(__, newValue) => setModalityContractType(newValue)}
                    renderInput={(params) => (
                      <TextField {...params} label="Tipo de contrato de la modalidad" />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <TextField
                    required
                    multiline
                    rows={5}
                    label="Objeto de contrato"
                    value={contractSubject}
                    InputProps={{ readOnly: !canEditBase }}
                    onChange={(evt) => setContractSubject(evt.target.value)}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <TextField
                    label="URL del SECOP"
                    value={secopUrl}
                    InputProps={{ readOnly: !canEditBase }}
                    onChange={(evt) => setSecopUrl(evt.target.value)}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <AnnexesUploader
                  bucket={process.env.REACT_APP_STORAGE_BUCKET_CONTRACTS}
                  referenceCode={contractCode}
                  annexes={annexes}
                  setAnnexes={setAnnexes}
                  disabled={!contractCode || [CONTRACT_STATUS.CLOSED].includes(status)}
                  defaultAnnexesList={annexesGroup.contract}
                />
              </Grid>
              <Grid item xs={6}>
                <Button
                  disabled={!isManager && !isSupervisor && !isAdmin}
                  onClick={() => setShowPaymentPlanModal(true)}
                >
                  Plan de pagos (manual)
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  disabled={!isManager && !isSupervisor && !isAdmin}
                  onClick={() => setShowTasksModal(true)}
                >
                  Obligaciones del contratista
                </Button>
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info">
                  El plan de pagos es manual y NO es obligatorio para aprobar el contrato:
                  la empresa solicita sus pagos por factura según lo acordado.
                </Alert>
              </Grid>
            </Grid>

            {/* Novedades: solo con el contrato ya aprobado, igual que en el
                flujo natural (antes de aprobar el contrato se edita directo).
                El bloqueo se calcula con el estado EFECTIVO para que un
                contrato con terminación anticipada ya vencida quede en
                consulta aunque el documento siga diciendo APPROVED. */}
            {[CONTRACT_STATUS.APPROVED, CONTRACT_STATUS.CLOSED].includes(status)
              && <Grid
                container
                spacing={2}
                sx={{ my: 2, borderTop: "thin solid lightgray" }}
              >
                <Grid item xs={12}>
                  <ContractNews
                    disabled={
                      !contractCode
                      || getContractFieldWithNews("status") === CONTRACT_STATUS.CLOSED
                      || (!isManager && !isSupervisor && !isAdmin)
                    }
                    setNews={setNews}
                    getContractFieldWithNews={getContractFieldWithNews}
                    manualPaymentPlan
                    contract={{
                      contractBudget,
                      finishedAt: getContractFieldWithNews("finishedAt"),
                      news,
                      startedAt,
                      tasks
                    }}
                  />
                </Grid>
              </Grid>
            }
            <Box sx={{ mt: 5, display: "flex" }}>
              <Box sx={{ flex: 1 }}>
                <Button variant="outlined" onClick={() => navigate("/contracts")}>
                  Regresar
                </Button>
                <Button
                  variant="contained"
                  sx={{ mx: 1 }}
                  onClick={handleSubmit}
                  disabled={status === CONTRACT_STATUS.CLOSED}
                >
                  {!contractId ? "Crear Contrato" : "Actualizar"}
                </Button>
                {contractId && status === CONTRACT_STATUS.DRAFT && (
                  <Button
                    variant="contained"
                    onClick={() => setSendSupervisorInfo({ open: true, title: "¿Realmente deseas enviar este contrato al supervisor?" })}
                  >
                    {loading ? <CircularProgress size={16} /> : "Enviar a supervisor"}
                  </Button>
                )}
                {contractId && status === CONTRACT_STATUS.SUPERVISOR && (
                  <>
                    <Button
                      variant="contained"
                      onClick={handleApprove}
                      disabled={!isSupervisor && !isAdmin}
                    >
                      {loading ? <CircularProgress size={16} /> : "Enviar a contratista"}
                    </Button>
                    {(isSupervisor || isAdmin) && (
                      <Button
                        variant="text"
                        color="error"
                        sx={{ mx: 1 }}
                        onClick={() => setReturnInfo({ open: true, title: "¿Realmente deseas devolver este contrato?" })}
                      >
                        {loading ? <CircularProgress size={16} /> : "Devolver"}
                      </Button>
                    )}
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={6} sx={{ textAlign: "center" }}>
          <ContractFormPanel
            contractId={contractId}
            contractCode={contractCode}
            designationUrl={designationUrl}
            fileUrl={fileUrl}
            handleGetDataStartup={handleGetDataStartup}
            liquidationUrl={liquidationUrl}
            loading={loading}
            setDesignationUrl={setDesignationUrl}
            setDocumentText={setDocumentText}
            setFileUrl={setFileUrl}
            setFileHashes={setFileHashes}
            setLiquidationUrl={setLiquidationUrl}
            setLoading={setLoading}
            setStartupUrl={setStartupUrl}
            startupUrl={startupUrl}
            status={status}
            setInfoSnack={setInfoSnack}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default ContractLegalForm;
